/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { useEffect } from "react";
import { useState, forwardRef } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import Loading from "./Loading";
import Layout from "./Layout";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import { loginState, configState } from "../lib/State";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { AppBar, Toolbar, IconButton } from "@mui/material";
import CenterSelect from "./CenterSelect";
import get_login from "../lib/get_login";
import is_admin from "../lib/is_admin";
import ReportMarketingAdd from "./ReportMarketingAdd";
import ReportMarketingEdit from "./ReportMarketingEdit";
import XChangeData from "./XChangeData";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ReportMarketingQuestion() {
  const [login, setLogin] = useState(loginState);
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [seq, setSeq] = useState(-1);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const token = localStorage.getItem("token");
  const [tcid, setTcid] = useState("");
  const state_category = ["제출", "마감"];
  const [data, setData] = useState(null); // 검색용 데이타

  const columns = [
    { field: "id", headerName: "id", hide: true },
    { field: "regdate", headerName: "등록일", width: 120 },
    { field: "state", headerName: "상태", width: 120 },
    { field: "score", headerName: "점수", width: 120 },
    { field: "title", headerName: "제목", width: 240 },
    { field: "cname", headerName: "센터명", width: 180 },
  ];

  const onCloseAddPop = () => {
    setShowAdd(false);
    load_data();
  };

  const onCloseEditPop = () => {
    setShowEdit(false);
    load_data();
  };

  const onOpenAdd = () => {
    setShowAdd(true);
  };

  // 리스트
  const load_data = async () => {
    let result = await axios.post(
      `${process.env.REACT_APP_API}/gympass_admin/ma_report`,
      {
        token: token,
        cid: login.cid,
        level: login.level,
        mcd: login.mcd,
        tcid: tcid,
        ...data,
      }
    );
    if (result.data.success === 1) {
      setRows(result.data.rows);
    } else {
      Swal.fire(result.data.msg);
      return;
    }
  };

  // 로그인
  useEffect(() => {
    const start = async () => {
      let result;
      result = await get_login();
      setLogin(result.data.login);
      result = await is_admin(result.data.login);
      if (!result) {
        window.location.href = "/";
      }
      setLoaded(true);
    };
    start();
  }, []);

  // 리스트
  useEffect(() => {
    if (!loaded) return;
    if (login.level === "500") return load_data();
    else if (tcid !== "") return load_data();
  }, [loaded, tcid]);

  return loaded === false ? (
    <Loading></Loading>
  ) : (
    <Layout login={login}>
      <h3
        css={css`
          margin-top: 10px;
        `}
      >
        마케팅 활동 보고서
      </h3>

      <Box
        display="flex"
        css={css`
          text-align: right;
          max-width: 1300px;
          margin-bottom: 15px;
        `}
      >
        {/* 센터명 */}
        <CenterSelect login={login} tcidParent={tcid} setTcidParent={setTcid} />
        <Box sx={{ flexGrow: 1 }}></Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onOpenAdd}
        >
          보고서추가
        </Button>
      </Box>

      {/* 옵션 */}
      <Box display="flex" sx={{ mb: 1, gap: 1 }}>
        <Box>
          <TextField
            sx={{ width: 160 }}
            name="date"
            label="점검일"
            size="small"
            onChange={(e) => {
              XChangeData(e, setData);
            }}
            autoComplete="off"
            value={data?.date || ""}
          />
        </Box>
        <Box>
          <TextField
            sx={{ width: 160 }}
            name="title"
            label="제목"
            size="small"
            onChange={(e) => {
              XChangeData(e, setData);
            }}
            autoComplete="off"
            value={data?.title || ""}
          />
        </Box>
        <FormControl>
          <InputLabel id="demo-simple-select-label" size="small">
            상태
          </InputLabel>
          <Select
            name="state"
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            label="상태"
            sx={{ width: 160 }}
            size="small"
            value={data?.state || ""}
            onChange={(e) => {
              XChangeData(e, setData);
            }}
          >
            {state_category.map((item, index) => {
              return (
                <MenuItem key={index} value={item}>
                  {item}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Button
          sx={{ fontSize: 15 }}
          variant="contained"
          onClick={load_data}
          size="small"
        >
          조회
        </Button>
      </Box>

      <DataGrid
        css={css`
          max-width: 1300px;
          min-height: 500px;
        `}
        rows={rows}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[5, 10]}
        checkboxSelection={false}
        onRowClick={(params) => {
          setSeq(params.row.id);
          setShowEdit(true);
        }}
        components={{ Toolbar: GridToolbar }}
      />

      <Dialog
        open={showAdd}
        fullWidth={true}
        maxWidth="md"
        TransitionComponent={Transition}
        onClose={() => {}}
      >
        <AppBar sx={{ position: "static" }}>
          <Toolbar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography style={{ fontSize: 18 }}>
                마케팅 보고서 추가
              </Typography>
            </Box>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onCloseAddPop}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <ReportMarketingAdd login={login} onClose={onCloseAddPop} seq={seq} />
      </Dialog>

      <Dialog
        open={showEdit}
        TransitionComponent={Transition}
        onClose={() => {}}
        maxWidth="md"
        fullWidth={true}
      >
        <AppBar sx={{ position: "static" }}>
          <Toolbar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography style={{ fontSize: 18 }}>
                마케팅 보고서 수정
              </Typography>
            </Box>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onCloseEditPop}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <ReportMarketingEdit login={login} seq={seq} onClose={onCloseEditPop} />
      </Dialog>
    </Layout>
  );
}
